(() => {
  'use strict';

  if (document.documentElement.dataset.lnVitaPage !== 'true') return;

  const initialize = root => {
    if (root.dataset.lnVitaReady === 'true' || root.closest('[hidden]') || getComputedStyle(root).display === 'none') return;
    root.dataset.lnVitaReady = 'true';
    const controller = new AbortController();
    const observers = [];
    const intervals = [];
    const query = (selector, parent = root) => parent.querySelector(selector);
    const queryAll = (selector, parent = root) => [...parent.querySelectorAll(selector)];
    const on = (target, event, listener, options = {}) => target?.addEventListener(event, listener, { ...options, signal: controller.signal });
    const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const behavior = () => reducedMotion.matches ? 'auto' : 'smooth';
    const byId = identifier => queryAll('[id]').find(element => element.id === identifier);
    const cleanups = [];
    const galleryControls = [];
    const dispose = () => {
      controller.abort();
      observers.forEach(observer => observer.disconnect());
      intervals.forEach(interval => clearInterval(interval));
      cleanups.forEach(cleanup => cleanup());
      delete root.dataset.lnVitaReady;
    };
    on(document, 'shopify:section:unload', event => {
      if (event.target.contains(root)) dispose();
    });

    queryAll('seika-gallery').forEach(gallery => {
      const stage = query('.gallery-stage', gallery);
      const slides = queryAll('[data-gallery-slide]', gallery);
      if (!stage || !slides.length) return;
      const phone = matchMedia('(max-width: 800px)');
      const thumbnails = queryAll('[data-thumb]', gallery);
      const dots = queryAll('.gallery-pager span', gallery);
      const strip = query('.gallery-strip', gallery);
      const zoomButton = query('[data-zoom-open]', gallery);
      const viewer = query('[data-zoom-viewer]', gallery);
      let current = clamp(Number(gallery.dataset.start) || 0, 0, slides.length - 1);
      let pendingFrame = 0;
      const offset = index => slides[index].offsetLeft - slides[0].offsetLeft;
      const activate = index => {
        const next = clamp(index, 0, slides.length - 1);
        if (next !== current) queryAll('video', slides[current]).forEach(video => video.pause());
        current = next;
        [current - 1, current, current + 1].forEach(position => {
          if (slides[position]) queryAll('img', slides[position]).forEach(image => { image.loading = 'eager'; });
        });
        if (zoomButton) zoomButton.hidden = !slides[current].hasAttribute('data-zoom');
        dots.forEach((dot, index) => dot.classList.toggle('is-active', index === current));
        thumbnails.forEach((thumbnail, index) => thumbnail.setAttribute('aria-current', String(index === current)));
        const thumbnail = thumbnails[current];
        if (strip && thumbnail && strip.clientWidth) {
          const padding = parseFloat(getComputedStyle(strip).paddingLeft) || 0;
          const left = thumbnail.offsetLeft;
          const right = left + thumbnail.offsetWidth;
          if (left - padding < strip.scrollLeft) strip.scrollLeft = left - padding;
          else if (right + padding > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right + padding - strip.clientWidth;
        }
      };
      const goTo = (index, smooth = true) => {
        activate(index);
        if (phone.matches) {
          stage.scrollTo({ left: Math.max(0, Math.min(offset(current), stage.scrollWidth - stage.clientWidth)), behavior: smooth ? behavior() : 'auto' });
        } else {
          slides.forEach((slide, position) => slide.classList.toggle('is-main', position === current));
          queryAll('img', slides[current]).forEach(image => { image.sizes = '(max-width: 800px) 84vw, 50vw'; });
        }
      };
      galleryControls.push({ gallery, goTo, slides });
      const align = () => goTo(current, false);
      on(stage, 'scroll', () => {
        if (pendingFrame || !phone.matches) return;
        pendingFrame = requestAnimationFrame(() => {
          pendingFrame = 0;
          if (!root.isConnected) return;
          let nearest = 0;
          let distance = Infinity;
          slides.forEach((slide, index) => {
            const difference = Math.abs(offset(index) - stage.scrollLeft);
            if (difference < distance) { distance = difference; nearest = index; }
          });
          activate(nearest);
        });
      }, { passive: true });
      on(strip, 'click', event => {
        const thumbnail = event.target.closest('[data-thumb]');
        if (thumbnail && strip.contains(thumbnail)) goTo(Number(thumbnail.dataset.thumb) || 0);
      });
      on(stage, 'click', event => {
        if (phone.matches || event.target.closest('video, button, a')) return;
        const slide = event.target.closest('[data-gallery-slide]');
        if (slide && slides.includes(slide)) goTo(slides.indexOf(slide));
      });
      if (!stage.hasAttribute('tabindex')) stage.tabIndex = 0;
      on(stage, 'keydown', event => {
        if (event.target.closest('video, input, select, textarea')) return;
        const directions = { ArrowLeft: -1, ArrowRight: 1 };
        if (Object.hasOwn(directions, event.key)) { event.preventDefault(); goTo(current + directions[event.key]); }
        if (event.key === 'Home') { event.preventDefault(); goTo(0); }
        if (event.key === 'End') { event.preventDefault(); goTo(slides.length - 1); }
      });
      on(phone, 'change', align);
      on(window, 'resize', align, { passive: true });
      requestAnimationFrame(align);
      cleanups.push(() => cancelAnimationFrame(pendingFrame));

      const zoomTrack = viewer && query('[data-zoom-track]', viewer);
      const zoomSlides = viewer ? queryAll('[data-zoom-slide]', viewer) : [];
      if (!viewer || !zoomButton || !zoomTrack || !zoomSlides.length) return;
      const zoomCount = query('[data-zoom-count]', viewer);
      let zoomIndex = 0;
      let scale = 1;
      let translateX = 0;
      let translateY = 0;
      let pinch = null;
      let pan = null;
      let moved = false;
      let lastTap = 0;
      let zoomFrame = 0;
      const zoomImage = () => query('img', zoomSlides[zoomIndex]);
      const renderZoom = animate => {
        const image = zoomImage();
        if (!image) return;
        image.classList.toggle('is-animating', animate && !reducedMotion.matches);
        image.style.transform = scale > 1 ? `translate(${translateX}px, ${translateY}px) scale(${scale})` : '';
        zoomSlides[zoomIndex].classList.toggle('is-zoomed', scale > 1.01);
        zoomTrack.classList.toggle('is-zoomed', scale > 1.01);
      };
      const resetZoom = (animate = false) => {
        scale = 1;
        translateX = 0;
        translateY = 0;
        renderZoom(animate);
      };
      const boundZoom = () => {
        const image = zoomImage();
        if (!image) return;
        const maximumX = Math.max(0, (image.offsetWidth * scale - zoomSlides[zoomIndex].clientWidth) / 2);
        const maximumY = Math.max(0, (image.offsetHeight * scale - zoomSlides[zoomIndex].clientHeight) / 2);
        translateX = clamp(translateX, -maximumX, maximumX);
        translateY = clamp(translateY, -maximumY, maximumY);
      };
      const point = (clientX, clientY) => {
        const rectangle = zoomSlides[zoomIndex].getBoundingClientRect();
        return { horizontal: clientX - rectangle.left - rectangle.width / 2, vertical: clientY - rectangle.top - rectangle.height / 2 };
      };
      const selectZoom = index => {
        resetZoom();
        zoomIndex = clamp(index, 0, zoomSlides.length - 1);
        if (zoomCount) zoomCount.textContent = String(zoomIndex + 1);
        [zoomIndex - 1, zoomIndex, zoomIndex + 1].forEach(position => {
          const image = zoomSlides[position] && query('img', zoomSlides[position]);
          if (image) image.loading = 'eager';
        });
      };
      const touchDistance = (first, second) => Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) || 1;
      on(zoomButton, 'click', () => {
        const originalIndex = Number(slides[current].dataset.gallerySlide);
        const position = zoomSlides.findIndex(slide => Number(slide.dataset.for) === originalIndex);
        if (position < 0 || viewer.open) return;
        viewer.showModal();
        document.documentElement.classList.add('gallery-zoom-open');
        selectZoom(position);
        zoomTrack.scrollLeft = zoomIndex * zoomTrack.clientWidth;
        query('[data-zoom-close]', viewer)?.focus({ preventScroll: true });
      });
      on(viewer, 'close', () => {
        resetZoom();
        document.documentElement.classList.remove('gallery-zoom-open');
        const position = slides.findIndex(slide => slide.dataset.gallerySlide === zoomSlides[zoomIndex].dataset.for);
        if (position >= 0) goTo(position, false);
        zoomButton.focus({ preventScroll: true });
      });
      on(viewer, 'click', event => {
        if (event.target.closest('[data-zoom-close]') || event.target === viewer) { viewer.close(); return; }
        if (!event.target.closest('[data-zoom-slide]') || moved) return;
        const now = Date.now();
        if (now - lastTap > 320) { lastTap = now; return; }
        lastTap = 0;
        if (scale > 1.01) { resetZoom(true); return; }
        const center = point(event.clientX, event.clientY);
        scale = 2.5;
        translateX = -1.5 * center.horizontal;
        translateY = -1.5 * center.vertical;
        boundZoom();
        renderZoom(true);
      });
      on(viewer, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        selectZoom(zoomIndex + (event.key === 'ArrowRight' ? 1 : -1));
        zoomTrack.scrollTo({ left: zoomIndex * zoomTrack.clientWidth, behavior: behavior() });
      });
      on(zoomTrack, 'scroll', () => {
        if (zoomFrame) return;
        zoomFrame = requestAnimationFrame(() => {
          zoomFrame = 0;
          const position = clamp(Math.round(zoomTrack.scrollLeft / (zoomTrack.clientWidth || 1)), 0, zoomSlides.length - 1);
          if (position !== zoomIndex) selectZoom(position);
        });
      }, { passive: true });
      on(viewer, 'touchstart', event => {
        if (!event.target.closest('[data-zoom-slide]')) return;
        moved = false;
        if (event.touches.length === 2) {
          const [first, second] = event.touches;
          pinch = { distance: touchDistance(first, second), scale, translateX, translateY, center: point((first.clientX + second.clientX) / 2, (first.clientY + second.clientY) / 2) };
          pan = null;
          event.preventDefault();
        } else if (event.touches.length === 1 && scale > 1.01) {
          pan = { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY, translateX, translateY };
        }
      }, { passive: false });
      on(viewer, 'touchmove', event => {
        if (pinch && event.touches.length === 2) {
          event.preventDefault();
          moved = true;
          const [first, second] = event.touches;
          scale = clamp(pinch.scale * touchDistance(first, second) / pinch.distance, 1, 4);
          const center = point((first.clientX + second.clientX) / 2, (first.clientY + second.clientY) / 2);
          translateX = center.horizontal - (pinch.center.horizontal - pinch.translateX) * scale / pinch.scale;
          translateY = center.vertical - (pinch.center.vertical - pinch.translateY) * scale / pinch.scale;
          boundZoom();
          renderZoom(false);
        } else if (pan && event.touches.length === 1) {
          event.preventDefault();
          moved = true;
          translateX = pan.translateX + event.touches[0].clientX - pan.clientX;
          translateY = pan.translateY + event.touches[0].clientY - pan.clientY;
          boundZoom();
          renderZoom(false);
        }
      }, { passive: false });
      on(viewer, 'touchend', event => {
        if (pinch && event.touches.length < 2) {
          pinch = null;
          if (scale < 1.05) resetZoom(true);
          else if (event.touches.length === 1) pan = { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY, translateX, translateY };
        }
        if (!event.touches.length) pan = null;
      });
      on(viewer, 'touchcancel', () => { pinch = null; pan = null; });
      cleanups.push(() => { cancelAnimationFrame(zoomFrame); if (viewer.open) viewer.close(); document.documentElement.classList.remove('gallery-zoom-open'); });
    });

    const evidencePanels = new Map();
    queryAll('seika-evidence').forEach(host => {
      const dialog = query('dialog', host);
      const body = query('[data-evidence-body]', host);
      const tabs = queryAll('[data-evidence-tab]', host);
      const sections = queryAll('[data-evidence-section]', host);
      if (!dialog || !body || !sections.length) return;
      let opener = null;
      const mark = name => tabs.forEach(tab => {
        const active = tab.dataset.evidenceTab === name;
        tab.classList.toggle('is-active', active);
        if (active) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });
      const sectionTop = section => section.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
      const go = (name, smooth = true) => {
        const section = sections.find(element => element.dataset.evidenceSection === name) || sections[0];
        body.scrollTo({ top: section === sections[0] ? 0 : sectionTop(section) - 8, behavior: smooth ? behavior() : 'auto' });
        mark(section.dataset.evidenceSection);
      };
      const open = (name, trigger) => {
        opener = trigger || document.activeElement;
        if (!dialog.open) dialog.showModal();
        document.documentElement.classList.add('seika-evidence-open');
        go(name || 'context', false);
        query('[data-evidence-close]', host)?.focus({ preventScroll: true });
      };
      evidencePanels.set(host, open);
      tabs.forEach(tab => on(tab, 'click', () => go(tab.dataset.evidenceTab)));
      on(query('[data-evidence-close]', host), 'click', () => dialog.close());
      on(dialog, 'click', event => { if (event.target === dialog) dialog.close(); });
      on(dialog, 'close', () => {
        document.documentElement.classList.remove('seika-evidence-open');
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
      on(body, 'scroll', () => {
        let active = sections[0];
        sections.forEach(section => { if (sectionTop(section) <= body.scrollTop + 60) active = section; });
        if (body.scrollTop + body.clientHeight >= body.scrollHeight - 4) active = sections[sections.length - 1];
        mark(active.dataset.evidenceSection);
      }, { passive: true });
      cleanups.push(() => { if (dialog.open) dialog.close(); document.documentElement.classList.remove('seika-evidence-open'); });
    });
    on(root, 'click', event => {
      const trigger = event.target.closest('[data-evidence-open], [data-sit-evidence], [data-sticky-evidence]');
      if (!trigger || !root.contains(trigger)) return;
      const controlled = byId(trigger.getAttribute('aria-controls'))?.closest('seika-evidence');
      const host = controlled || evidencePanels.keys().next().value;
      const open = evidencePanels.get(host);
      if (!open) return;
      event.preventDefault();
      open(trigger.dataset.evidenceOpen || (trigger.hasAttribute('data-sit-evidence') ? 'references' : 'studies'), trigger);
    });
    queryAll('[data-sticky-evidence]').forEach(button => { button.hidden = !evidencePanels.size; });

    const bindTabs = (tabs, activate) => tabs.forEach((tab, index) => {
      on(tab, 'click', () => activate(index));
      on(tab, 'keydown', event => {
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(next);
        tabs[next].focus();
      });
    });
    queryAll('seika-info-tabs').forEach(host => {
      const tabs = queryAll('[role="tab"]', host);
      const activate = index => {
        tabs.forEach((tab, position) => {
          const active = index === position;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          const panel = byId(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !active;
        });
        const tab = tabs[index];
        if (tab) tab.parentElement.scrollTo({ left: tab.offsetLeft - (tab.parentElement.clientWidth - tab.offsetWidth) / 2, behavior: behavior() });
      };
      bindTabs(tabs, activate);
      if (tabs.length) activate(Math.max(0, tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true')));
      queryAll('.sit__ev-cards', host).forEach(track => {
        const dots = query('[data-sit-dots]', track.parentElement);
        const cards = [...track.children];
        if (!dots || cards.length < 2) return;
        dots.replaceChildren();
        const buttons = cards.map((card, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', `Mostra scheda ${index + 1} di ${cards.length}`);
          on(button, 'click', () => track.scrollTo({ left: card.offsetLeft - cards[0].offsetLeft, behavior: behavior() }));
          dots.append(button);
          return button;
        });
        const sync = () => {
          const maximum = track.scrollWidth - track.clientWidth;
          let active = 0;
          let distance = Infinity;
          cards.forEach((card, index) => {
            const difference = Math.abs(card.offsetLeft - cards[0].offsetLeft - track.scrollLeft);
            if (difference < distance) { distance = difference; active = index; }
          });
          if (maximum > 2 && track.scrollLeft >= maximum - 2) active = cards.length - 1;
          buttons.forEach((button, index) => button.setAttribute('aria-current', String(index === active)));
          dots.hidden = maximum <= 2;
        };
        on(track, 'scroll', sync, { passive: true });
        if ('ResizeObserver' in window) { const observer = new ResizeObserver(sync); observer.observe(track); observers.push(observer); }
        else on(window, 'resize', sync, { passive: true });
        sync();
      });
    });
    queryAll('.sk-sci').forEach(host => {
      const tabs = queryAll('.sk-sci__tab[data-tab]', host);
      const panels = queryAll('.sk-sci__panel[data-panel]', host);
      const activate = index => {
        tabs.forEach((tab, position) => {
          const active = position === index;
          tab.classList.toggle('is-on', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => {
          const active = panel.dataset.panel === tabs[index]?.dataset.tab;
          panel.classList.toggle('is-on', active);
          panel.hidden = !active;
        });
      };
      bindTabs(tabs, activate);
      if (tabs.length) activate(Math.max(0, tabs.findIndex(tab => tab.classList.contains('is-on'))));
    });

    queryAll('[data-lvz-instance]').forEach(host => {
      const track = query('[data-reviews-track]', host);
      const reviews = track && queryAll(':scope > [data-review]', track);
      if (!track || !reviews.length) return;
      const previous = query('[data-reviews-prev]', host);
      const next = query('[data-reviews-next]', host);
      let current = 0;
      let touchStart = null;
      const render = index => {
        current = clamp(index, 0, reviews.length - 1);
        track.style.transform = `translateX(-${current * 100}%)`;
        if (reducedMotion.matches) track.style.transition = 'none';
        if (previous) previous.disabled = current === 0;
        if (next) next.disabled = current === reviews.length - 1;
      };
      on(previous, 'click', () => render(current - 1));
      on(next, 'click', () => render(current + 1));
      on(track, 'touchstart', event => { touchStart = event.touches.length === 1 ? { horizontal: event.touches[0].clientX, vertical: event.touches[0].clientY } : null; }, { passive: true });
      on(track, 'touchend', event => {
        if (!touchStart || !event.changedTouches.length) return;
        const horizontal = event.changedTouches[0].clientX - touchStart.horizontal;
        const vertical = event.changedTouches[0].clientY - touchStart.vertical;
        if (Math.abs(horizontal) > 50 && Math.abs(horizontal) > Math.abs(vertical)) render(current + (horizontal < 0 ? 1 : -1));
        touchStart = null;
      }, { passive: true });
      on(track, 'touchcancel', () => { touchStart = null; });
      render(0);
    });
    queryAll('[data-wall-more]').forEach(button => on(button, 'click', () => {
      const wall = button.closest('.seika-wall, section') || root;
      queryAll('[data-wall-extra]', wall).forEach(card => { card.hidden = false; });
      button.parentElement.hidden = true;
    }));
    const revealElements = queryAll('.reveal');
    if ('IntersectionObserver' in window && !reducedMotion.matches) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }), { threshold: 0.12 });
      revealElements.forEach(element => observer.observe(element));
      observers.push(observer);
    } else revealElements.forEach(element => element.classList.add('visible'));
    try {
      const target = byId(decodeURIComponent(location.hash.slice(1)));
      if (target?.tagName === 'DETAILS') target.open = true;
    } catch {}

    const form = query('[data-ln-vita-form]');
    const addButtons = queryAll('[data-add], [data-secondary-add]');
    const statusNodes = queryAll('[data-ln-vita-status], [data-product-status], [data-bundle-status]');
    const setStatus = message => statusNodes.forEach(element => { element.textContent = message; element.hidden = !message; });
    const setDisabled = disabled => addButtons.forEach(button => {
      if ('disabled' in button) button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    });
    let product = null;
    try { product = JSON.parse(query('[data-ln-vita-product]')?.textContent || 'null'); } catch {}
    if (!form || !product || !Array.isArray(product.variants)) {
      setDisabled(true);
      setStatus('Questo prodotto non è al momento disponibile.');
    } else {
      const color = query('[data-ln-vita-color]');
      const formula = query('[data-ln-vita-formula]');
      const variantInput = query('input[name="id"]', form);
      const readOnly = document.body.dataset.lnDemo === 'true';
      const requested = new URLSearchParams(location.search).get('variant');
      let variant = product.variants.find(item => String(item.id) === requested) || product.variants.find(item => item.available === true) || product.variants[0];
      let busy = false;
      const money = new Intl.NumberFormat('it-IT', { style: 'currency', currency: product.currency || 'EUR' });
      const routePrefix = root.dataset.root || document.body.dataset.root || window.Shopify?.routes?.root || '/';
      let localRoot = '/';
      try {
        const route = new URL(routePrefix, location.origin);
        if (route.origin === location.origin) localRoot = route.pathname.replace(/\/?$/, '/');
      } catch {}
      const cartPath = `${localRoot}cart`;
      const available = () => !readOnly && product.available === true && variant?.available === true && /^\d+$/.test(String(variant.id));
      const selectGalleryColor = () => {
        const card = queryAll('[data-option-card]').find(button => button.dataset.optionCard === color?.value);
        const configured = card?.dataset.lnVitaGalleryIndex;
        const originalIndex = configured !== undefined ? Number(configured) : color?.value === 'Rosso passione' ? 3 : color?.value === 'Viola reale' ? 2 : -1;
        if (originalIndex < 0 || !Number.isFinite(originalIndex)) return;
        galleryControls.forEach(control => {
          const index = control.slides.findIndex(slide => Number(slide.dataset.gallerySlide) === originalIndex);
          if (index >= 0) control.goTo(index);
        });
      };
      const renderVariant = () => {
        variant = product.variants.find(item => (!color || item.options?.[0] === color.value) && (!formula || item.options?.[1] === formula.value));
        if (variantInput) variantInput.value = variant ? String(variant.id) : '';
        if (variant && Number.isFinite(Number(variant.price))) {
          queryAll('[data-price], [data-purchase-total], [data-ln-vita-price]').forEach(element => { element.textContent = money.format(Number(variant.price) / 100); });
          const comparison = Number(variant.compare_at_price || variant.compareAtPrice || 0);
          queryAll('[data-compare-price]').forEach(element => {
            element.textContent = comparison > Number(variant.price) ? money.format(comparison / 100) : '';
            const holder = element.closest('.was-price') || element;
            holder.hidden = comparison <= Number(variant.price);
          });
          queryAll('[data-savings]').forEach(element => {
            element.hidden = comparison <= Number(variant.price);
            element.textContent = comparison > Number(variant.price) ? `Risparmia ${money.format((comparison - Number(variant.price)) / 100)}` : '';
          });
        }
        queryAll('[data-option-card]').forEach(button => {
          const select = byId(button.dataset.selectId);
          if (select?.tagName === 'SELECT') button.setAttribute('aria-pressed', String(select.value === button.dataset.optionCard));
        });
        setDisabled(busy || !available());
        setStatus(readOnly ? 'Anteprima: gli acquisti sono disattivati.' : available() ? '' : 'Questa variante non è al momento disponibile.');
      };
      if (variant) {
        if (color && variant.options?.[0]) color.value = variant.options[0];
        if (formula && variant.options?.[1]) formula.value = variant.options[1];
      }
      on(color, 'change', () => { renderVariant(); selectGalleryColor(); });
      on(formula, 'change', renderVariant);
      queryAll('[data-option-card]').forEach(button => on(button, 'click', () => {
        const select = byId(button.dataset.selectId);
        if (!select || ![color, formula].includes(select)) return;
        const value = button.dataset.optionCard;
        if (![...select.options].some(option => option.value === value)) return;
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      queryAll('[data-secondary-add]').forEach(button => on(button, 'click', event => {
        event.preventDefault();
        if (!busy && available()) form.requestSubmit();
      }));
      on(form, 'submit', async event => {
        event.preventDefault();
        if (busy || !available()) return;
        busy = true;
        setDisabled(true);
        form.setAttribute('aria-busy', 'true');
        setStatus('Aggiunta al carrello in corso…');
        try {
          const response = await fetch(`${localRoot}cart/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: { 'Omaggi inclusi': 'Custodia protettiva, lubrificante, ebook e programma di intimità' } }] })
          });
          const result = await response.json();
          if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Impossibile aggiungere il prodotto. Riprova.');
          setStatus('Prodotto aggiunto al carrello.');
          const opener = [...document.querySelectorAll('[data-open-cart]')].find(button => !root.contains(button));
          if (opener) opener.click();
          else location.assign(cartPath);
        } catch (error) {
          setStatus(error instanceof Error ? error.message : 'Impossibile aggiungere il prodotto. Riprova.');
          queryAll('[data-cart-recovery]').forEach(link => { link.href = cartPath; link.hidden = false; });
        } finally {
          busy = false;
          form.removeAttribute('aria-busy');
          setDisabled(!available());
        }
      });
      renderVariant();
      selectGalleryColor();
    }

    queryAll('seika-sticky .sticky-purchase, [data-ln-vita-sticky]').forEach(bar => {
      const primary = query('[data-ln-vita-form] [data-add]');
      const bottom = query('.offer-copy [data-secondary-add]');
      let pendingFrame = 0;
      const draw = () => {
        if (!primary) { bar.hidden = true; return; }
        const primaryBounds = primary.getBoundingClientRect();
        const bottomBounds = bottom?.getBoundingClientRect();
        bar.hidden = primaryBounds.bottom > 90 || !!(bottomBounds && bottomBounds.top < window.innerHeight && bottomBounds.bottom > 0);
      };
      const schedule = () => {
        if (pendingFrame) return;
        pendingFrame = requestAnimationFrame(() => { pendingFrame = 0; draw(); });
      };
      on(window, 'scroll', schedule, { passive: true });
      on(window, 'resize', schedule, { passive: true });
      cleanups.push(() => cancelAnimationFrame(pendingFrame));
      draw();
    });

    const timers = queryAll('[data-deal-timer]');
    if (timers.length) {
      const romeClock = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
      const parts = timestamp => Object.fromEntries(romeClock.formatToParts(timestamp).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
      const nextMidnight = timestamp => {
        const today = parts(timestamp);
        const midnight = Date.UTC(today.year, today.month - 1, today.day + 1);
        let deadline = midnight;
        for (let attempt = 0; attempt < 3; attempt++) {
          const observed = parts(deadline);
          const correction = midnight - Date.UTC(observed.year, observed.month - 1, observed.day, observed.hour, observed.minute, observed.second);
          deadline += correction;
          if (!correction) break;
        }
        return deadline;
      };
      const tick = () => {
        const now = Date.now();
        const seconds = Math.ceil((nextMidnight(now) - now) / 1000);
        timers.forEach(timer => {
          const hours = query('[data-deal-h]', timer);
          const minutes = query('[data-deal-m]', timer);
          const remainingSeconds = query('[data-deal-s]', timer);
          if (hours) hours.textContent = String(Math.floor(seconds / 3600)).padStart(2, '0');
          if (minutes) minutes.textContent = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
          if (remainingSeconds) remainingSeconds.textContent = String(seconds % 60).padStart(2, '0');
        });
      };
      tick();
      intervals.push(setInterval(() => { if (!root.isConnected) dispose(); else tick(); }, 1000));
      on(document, 'visibilitychange', tick);
    }
  };

  document.querySelectorAll('[data-ln-vita-root]').forEach(initialize);
  document.addEventListener('shopify:section:load', () => {
    if (document.documentElement.dataset.lnVitaPage === 'true') document.querySelectorAll('[data-ln-vita-root]').forEach(initialize);
  });
})();
